type Vec3 = [number, number, number]

// Machado, Oliveira & Fernandes (2009), severity 1.0, applied in linear RGB.
const CVD: Record<'protan' | 'deutan', Vec3[]> = {
  protan: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998]
  ],
  deutan: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881]
  ]
}

export type Vision = 'normal' | 'protan' | 'deutan'

const toLinear = (hex: string): Vec3 =>
  [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as Vec3

const fromLinear = (rgb: Vec3): string =>
  '#' +
  rgb
    .map((c) => {
      const v = Math.max(0, Math.min(1, c))
      const s = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055
      return Math.round(s * 255)
        .toString(16)
        .padStart(2, '0')
    })
    .join('')

function simulateLinear(rgb: Vec3, vision: Vision): Vec3 {
  if (vision === 'normal') return rgb
  return CVD[vision].map((row) => Math.max(0, Math.min(1, row[0] * rgb[0] + row[1] * rgb[1] + row[2] * rgb[2]))) as Vec3
}

function oklab([r, g, b]: Vec3): Vec3 {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  ]
}

/** How [hex] looks under [vision]. */
export const simulate = (hex: string, vision: Vision): string => fromLinear(simulateLinear(toLinear(hex), vision))

/** OKLab ΔE ×100 under [vision]; ~15 reads as clearly different. */
export function deltaE(a: string, b: string, vision: Vision = 'normal'): number {
  const x = oklab(simulateLinear(toLinear(a), vision))
  const y = oklab(simulateLinear(toLinear(b), vision))
  return 100 * Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2])
}

export interface WorstPair {
  a: string
  b: string
  distance: number
  vision: Vision
}

/** The closest pair in [colors]; colour-blind takes the worse of protan and deutan. */
export function worstPair(colors: string[], kind: 'normal' | 'colorblind'): WorstPair {
  let worst: WorstPair = { a: colors[0]!, b: colors[0]!, distance: Infinity, vision: 'normal' }
  const visions: Vision[] = kind === 'normal' ? ['normal'] : ['protan', 'deutan']
  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      for (const vision of visions) {
        const distance = deltaE(colors[i]!, colors[j]!, vision)
        if (distance < worst.distance) worst = { a: colors[i]!, b: colors[j]!, distance, vision }
      }
    }
  }
  return worst
}
